import { WithdrawalMode } from '@generated/graphql';
import type { TranslatedString } from '@interfaces/shared';
import { AmountField } from '@ovComponents/3-pattern/amountField/amountField';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { dollars2cents } from '@utils/dollars2cents';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';

export type WithdrawalAmountOptions = {
  enabled?: boolean;
  required?: string;
  label?: TranslatedString;
};

type AmountFieldConfig = {
  testId: string;
  focusKey: string;
  label: string;
  cents: number | undefined;
  onChange: (cents: number | undefined) => void;
};

export type WithdrawalAmountFieldsProp = {
  amountOptions: WithdrawalAmountOptions | undefined;
  netAmountLabel: TranslatedString | undefined;
  mode: WithdrawalMode;
  amountCents: number | undefined;
  netAmountCents: number | undefined;
  currency: string | undefined;
  focused: string[];
  onFieldBlur: (field: string) => void;
  onAmountChange: (cents: number | undefined) => void;
  onNetAmountChange: (cents: number | undefined) => void;
};

export const WithdrawalAmountFields = ({
  amountOptions,
  netAmountLabel,
  mode,
  amountCents,
  netAmountCents,
  currency,
  focused,
  onFieldBlur,
  onAmountChange,
  onNetAmountChange,
}: WithdrawalAmountFieldsProp) => {
  const { t } = useTranslation('components');
  const { translateBackendString } = useLocalization();
  const isAmountEnabled = Boolean(amountOptions?.enabled);
  const isAmountRequired =
    amountOptions?.required !== FieldRequiredType.NOT_REQUIRED;

  if (!isAmountEnabled) return null;

  const fieldByMode: Record<WithdrawalMode, AmountFieldConfig> = {
    [WithdrawalMode.Gross]: {
      testId: 'withdraw-form-amount-cents',
      focusKey: 'amountCents',
      label: translateBackendString(amountOptions?.label),
      cents: amountCents,
      onChange: onAmountChange,
    },
    [WithdrawalMode.Net]: {
      testId: 'withdraw-form-net-amount-cents',
      focusKey: 'netAmountCents',
      label:
        translateBackendString(netAmountLabel) || t('transferModal.netAmount'),
      cents: netAmountCents,
      onChange: onNetAmountChange,
    },
  };
  const field = fieldByMode[mode];

  return (
    <AmountField
      key={mode}
      testId={field.testId}
      sx={{ mb: 2 }}
      label={field.label}
      useFormatAmountValueCents
      setAmount={(newAmount: string) =>
        field.onChange(newAmount ? dollars2cents(newAmount, 2) : undefined)
      }
      amount={field.cents}
      onBlur={() => onFieldBlur(field.focusKey)}
      error={
        !field.cents && focused.includes(field.focusKey) && isAmountRequired
      }
      currency={currency}
    />
  );
};
